import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/page-header";
import { FilterSidebar, type FilterGroup } from "@/components/filter-sidebar";
import { ListToolbar } from "@/components/list-toolbar";
import { PaginationBar } from "@/components/pagination-bar";
import { BulkActionMenu, type BulkActionItem } from "@/components/bulk-action-menu";
import { BulkModal } from "@/components/bulk-modal";
import { ColumnPicker, usePersistentColumns, type ColumnDef } from "@/components/column-picker";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useList } from "@/hooks/use-list";
import { useSelection } from "@/hooks/use-selection";
import { exportCSV } from "@/lib/csv-export";
import { api } from "@/lib/api";
import { Plus, Trash2, Upload, UserCheck, Edit3, Download, Send, Sparkles, Linkedin, ChevronDown, CheckSquare, ArrowRightCircle, PhoneCall } from "lucide-react";

type Filter = "all" | "new" | "contacted" | "qualified" | "converted" | "apollo";
type BulkMode = null | "delete" | "transfer" | "update" | "createTask" | "convert";

export default function LeadsPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [filter, setFilter] = useState<Filter>("all");
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(true);

  const sel = useSelection();

  // --- Column customization ---
  const LEAD_COLUMNS: ColumnDef[] = [
    { key: "name",         label: "Lead Name",   locked: true },
    { key: "company",      label: "Company" },
    { key: "title",        label: "Title",       optional: true },
    { key: "email",        label: "Email" },
    { key: "phone",        label: "Phone",       optional: true },
    { key: "source",       label: "Source" },
    { key: "status",       label: "Status" },
    { key: "score",        label: "Score" },
    { key: "leadIndustry", label: "Industry",    optional: true },
    { key: "city",         label: "City",        optional: true },
    { key: "state",        label: "State",       optional: true },
    { key: "country",      label: "Country",     optional: true },
    { key: "linkedin",     label: "LinkedIn",    optional: true },
    { key: "createdAt",    label: "Created",     optional: true },
  ];
  const defaultCols = new Set(LEAD_COLUMNS.filter((c) => !c.optional).map((c) => c.key));
  const [visibleCols, setVisibleCols] = usePersistentColumns("leads.columns.v1", defaultCols);
  const isColOn = (k: string) => LEAD_COLUMNS.find((c) => c.key === k)?.locked || visibleCols.has(k);

  const extraParams = useMemo(() => {
    const p: Record<string, unknown> = {};
    if (filter === "new")       p.status = "NEW";
    if (filter === "contacted") p.status = "CONTACTED";
    if (filter === "qualified") p.status = "QUALIFIED";
    if (filter === "converted") p.status = "CONVERTED";
    if (filter === "apollo")    p.source = "Apollo";
    if (ownerId)                p.ownerId = ownerId;
    return p;
  }, [filter, ownerId]);

  const { data, isLoading } = useList<any>("/leads", { q, page, pageSize, sortBy, sortDir, ...extraParams });
  const rows = data?.data ?? [];
  const total = data?.pagination?.total;
  const visibleIds = rows.map((r: any) => r.id);

  useEffect(() => { sel.clear(); /* eslint-disable-next-line */ }, [filter, q, page, ownerId]);

  const users = useQuery({
    queryKey: ["/users"],
    queryFn: async () => (await api.get("/users")).data.data as { id: string; name: string }[],
  });

  async function convert(id: string) {
    await api.post(`/leads/${id}/convert`);
    qc.invalidateQueries({ queryKey: ["/leads"] });
  }
  async function del(id: string, name: string) {
    if (!confirm(`Delete lead "${name}"?`)) return;
    await api.delete(`/leads/${id}`);
    qc.invalidateQueries({ queryKey: ["/leads"] });
  }

  // --- Bulk operations ---
  const [bulkMode, setBulkMode] = useState<BulkMode>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [transferOwnerId, setTransferOwnerId] = useState("");
  const [updateField, setUpdateField] = useState<"status" | "source" | "leadIndustry" | "leadDegree" | "leadGeography">("status");
  const [updateValue, setUpdateValue] = useState("");

  // Bulk create-task form fields
  const [taskSubject, setTaskSubject] = useState("");
  const [taskDueAt, setTaskDueAt] = useState("");
  const [taskPriority, setTaskPriority] = useState<"LOW" | "NORMAL" | "HIGH">("NORMAL");
  const [taskBody, setTaskBody] = useState("");

  const bulkMut = useMutation({
    mutationFn: async () => {
      const ids = sel.ids;
      if (bulkMode === "delete") {
        await api.post("/leads/bulk-delete", { ids });
      } else if (bulkMode === "transfer") {
        await api.post("/leads/bulk-transfer", { ids, ownerId: transferOwnerId });
      } else if (bulkMode === "update") {
        await api.post("/leads/bulk-update", { ids, field: updateField, value: updateValue });
      } else if (bulkMode === "createTask") {
        await api.post("/leads/bulk-create-task", {
          ids,
          subject: taskSubject,
          body: taskBody || undefined,
          priority: taskPriority,
          dueAt: taskDueAt ? new Date(taskDueAt).toISOString() : undefined,
        });
      } else if (bulkMode === "convert") {
        await api.post("/leads/bulk-convert", { ids: ids.slice(0, 25) });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/leads"] });
      qc.invalidateQueries({ queryKey: ["/activities"] });
      qc.invalidateQueries({ queryKey: ["/deals"] });
      qc.invalidateQueries({ queryKey: ["/contacts"] });
      qc.invalidateQueries({ queryKey: ["/accounts"] });
      sel.clear();
      setBulkMode(null);
      setBulkError(null);
      setTaskSubject(""); setTaskBody(""); setTaskDueAt(""); setTaskPriority("NORMAL");
    },
    onError: (err: any) => setBulkError(err?.response?.data?.message ?? "Action failed."),
  });

  function doExport() {
    const selected = sel.count > 0 ? rows.filter((r: any) => sel.isSelected(r.id)) : rows;
    exportCSV(`leads-${new Date().toISOString().slice(0, 10)}`, selected, [
      { key: "name",       label: "Name" },
      { key: "company",    label: "Company" },
      { key: "email",      label: "Email" },
      { key: "phone",      label: "Phone" },
      { key: "mobile",     label: "Mobile" },
      { key: "source",     label: "Source" },
      { key: "status",     label: "Status" },
      { key: "score",      label: "Score" },
      { key: "leadIndustry",  label: "Industry" },
      { key: "leadGeography", label: "Geography" },
      { key: "leadDegree",    label: "Degree" },
      { key: "createdAt",  label: "Created" },
    ]);
  }

  function massEmail() {
    // Build a mailto with the emails as BCC so Outlook (or default client) opens.
    const selected = rows.filter((r: any) => sel.isSelected(r.id));
    const emails = selected.map((r: any) => r.email).filter(Boolean);
    if (emails.length === 0) { alert("No selected lead has an email address."); return; }
    window.open(`mailto:?bcc=${encodeURIComponent(emails.join(","))}`, "_blank");
  }

  async function addToQueue() {
    const ids = sel.ids;
    if (ids.length === 0) return;
    if (!confirm(`Add ${ids.length} lead${ids.length > 1 ? "s" : ""} to your call queue? They'll be reassigned to you.`)) return;
    await api.post("/call-queue/add", { kind: "lead", ids });
    qc.invalidateQueries({ queryKey: ["/leads"] });
    qc.invalidateQueries({ queryKey: ["/call-queue"] });
    sel.clear();
  }

  const bulkItems: BulkActionItem[] = [
    { label: "Create Task",       icon: <CheckSquare className="h-4 w-4"/>,     requiresSelection: 1, onClick: () => setBulkMode("createTask") },
    { label: "Add to Call Queue", icon: <PhoneCall className="h-4 w-4"/>,        requiresSelection: 1, onClick: addToQueue },
    { label: "Mass Convert",      icon: <ArrowRightCircle className="h-4 w-4"/>, requiresSelection: 1, onClick: () => setBulkMode("convert") },
    { label: "Mass Transfer",     icon: <UserCheck className="h-4 w-4"/>,       requiresSelection: 1, onClick: () => setBulkMode("transfer") },
    { label: "Mass Update",       icon: <Edit3 className="h-4 w-4"/>,           requiresSelection: 1, onClick: () => setBulkMode("update") },
    { label: "Mass Email",        icon: <Send className="h-4 w-4"/>,            requiresSelection: 1, onClick: massEmail },
    { label: "Mass Delete",       icon: <Trash2 className="h-4 w-4"/>,          requiresSelection: 1, onClick: () => setBulkMode("delete"), danger: true },
    { label: "Export to CSV",     icon: <Download className="h-4 w-4"/>,        onClick: doExport, divideAbove: true },
  ];

  const groups: FilterGroup[] = [
    {
      title: "Status",
      items: [
        { label: "All leads",     active: filter === "all",       onClick: () => setFilter("all"), count: total },
        { label: "New",           active: filter === "new",       onClick: () => setFilter("new") },
        { label: "Contacted",     active: filter === "contacted", onClick: () => setFilter("contacted") },
        { label: "Qualified",     active: filter === "qualified", onClick: () => setFilter("qualified") },
        { label: "Converted",     active: filter === "converted", onClick: () => setFilter("converted") },
      ],
    },
    {
      title: "Source",
      items: [
        { label: "From Apollo",   active: filter === "apollo",    onClick: () => setFilter("apollo") },
      ],
    },
    {
      title: "Owner",
      items: [
        { label: "All owners", active: !ownerId, onClick: () => setOwnerId(null) },
        ...(users.data ?? []).map((u) => ({ label: u.name, active: ownerId === u.id, onClick: () => setOwnerId(u.id) })),
      ],
    },
  ];

  const updateOptions: Record<string, string[]> = {
    status: ["NEW", "CONTACTED", "QUALIFIED", "NURTURING", "DISQUALIFIED"],
    source: ["Apollo", "LinkedIn", "Webinar", "Event", "Partner", "Website", "Referral", "Cold Call", "Email Campaign"],
    leadDegree: ["Hot", "Warm", "Cold"],
    leadGeography: ["NA", "EMEA", "APAC", "LATAM", "India"],
    leadIndustry: [],
  };

  return (
    <>
      <PageHeader
        title="Leads"
        description="Top-of-funnel prospects from Apollo, LinkedIn, forms, events."
        actions={
          <>
            <FindProspectsMenu />
            <Button asChild variant="outline"><Link to="/leads/import"><Upload className="mr-1 h-4 w-4"/>Import CSV</Link></Button>
            <Button asChild><Link to="/leads/new"><Plus className="mr-1 h-4 w-4"/>Create Lead</Link></Button>
            <BulkActionMenu selectedCount={sel.count} items={bulkItems} />
          </>
        }
      />

      <ListToolbar
        filtersOpen={filtersOpen}
        onToggleFilters={() => setFiltersOpen((v) => !v)}
        sortBy={sortBy}
        sortDir={sortDir}
        sortOptions={[
          { value: "createdAt",   label: "Created" },
          { value: "updatedAt",   label: "Last modified" },
          { value: "name",        label: "Name" },
          { value: "score",       label: "Score" },
        ]}
        onSortChange={(by, dir) => { setSortBy(by); setSortDir(dir); }}
        rightSlot={
          <div className="flex items-center gap-2">
            <Input placeholder="Search leads…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="h-8 w-64" />
            <ColumnPicker storageKey="leads.columns.v1" columns={LEAD_COLUMNS} visible={visibleCols} onChange={setVisibleCols} />
          </div>
        }
      />

      <div className="flex flex-1 min-h-[60vh]">
        {filtersOpen && <FilterSidebar groups={groups} onClear={() => setFilter("all")} />}

        <div className="flex flex-1 flex-col min-w-0">
          <div className="flex-1 overflow-auto">
            <Table>
              <Thead>
                <Tr>
                  <Th className="w-10 sticky left-0 bg-card z-10">
                    <input
                      type="checkbox"
                      checked={sel.allSelected(visibleIds)}
                      onChange={() => sel.toggleAll(visibleIds)}
                    />
                  </Th>
                  <Th className="sticky left-10 bg-card z-10">Lead Name</Th>
                  {isColOn("company")      && <Th>Company</Th>}
                  {isColOn("title")        && <Th>Title</Th>}
                  {isColOn("email")        && <Th>Email</Th>}
                  {isColOn("phone")        && <Th>Phone</Th>}
                  {isColOn("source")       && <Th>Source</Th>}
                  {isColOn("status")       && <Th>Status</Th>}
                  {isColOn("score")        && <Th>Score</Th>}
                  {isColOn("leadIndustry") && <Th>Industry</Th>}
                  {isColOn("city")         && <Th>City</Th>}
                  {isColOn("state")        && <Th>State</Th>}
                  {isColOn("country")      && <Th>Country</Th>}
                  {isColOn("linkedin")     && <Th>LinkedIn</Th>}
                  {isColOn("createdAt")    && <Th>Created</Th>}
                  <Th></Th>
                  <Th className="w-10"></Th>
                </Tr>
              </Thead>
              <Tbody>
                {isLoading && <Tr><Td colSpan={20} className="py-8 text-center text-muted-foreground">Loading…</Td></Tr>}
                {!isLoading && rows.length === 0 && (
                  <Tr><Td colSpan={20} className="py-12 text-center text-muted-foreground">No leads match this view.</Td></Tr>
                )}
                {rows.map((l: any) => {
                  const linkedinCount = (l.linkedinUrls?.length ?? 0) + (l.linkedinUrl && !l.linkedinUrls?.includes(l.linkedinUrl) ? 1 : 0);
                  return (
                  <Tr key={l.id} className="group">
                    <Td className="sticky left-0 bg-background z-10">
                      <input type="checkbox" checked={sel.isSelected(l.id)} onChange={() => sel.toggle(l.id)} />
                    </Td>
                    <Td className="font-medium sticky left-10 bg-background z-10"><Link to={`/leads/${l.id}`} className="hover:underline">{l.name}</Link></Td>
                    {isColOn("company")      && <Td>{l.company ?? "—"}</Td>}
                    {isColOn("title")        && <Td className="text-muted-foreground">{l.title ?? "—"}</Td>}
                    {isColOn("email")        && <Td className="text-muted-foreground">{l.email ?? "—"}</Td>}
                    {isColOn("phone")        && <Td className="text-muted-foreground">{l.phone ?? l.mobile ?? "—"}</Td>}
                    {isColOn("source")       && <Td>{l.source ?? "—"}</Td>}
                    {isColOn("status")       && <Td><Badge variant="secondary">{l.status}</Badge></Td>}
                    {isColOn("score")        && <Td className="tabular-nums">{l.score}</Td>}
                    {isColOn("leadIndustry") && <Td className="text-muted-foreground">{l.leadIndustry ?? "—"}</Td>}
                    {isColOn("city")         && <Td className="text-muted-foreground">{l.city ?? "—"}</Td>}
                    {isColOn("state")        && <Td className="text-muted-foreground">{l.state ?? "—"}</Td>}
                    {isColOn("country")      && <Td className="text-muted-foreground">{l.country ?? "—"}</Td>}
                    {isColOn("linkedin")     && (
                      <Td>
                        {l.linkedinUrl ? (
                          <a href={l.linkedinUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                            Profile{linkedinCount > 1 && <span className="ml-1 text-[10px] text-muted-foreground">+{linkedinCount - 1}</span>}
                          </a>
                        ) : "—"}
                      </Td>
                    )}
                    {isColOn("createdAt")    && <Td className="text-muted-foreground">{new Date(l.createdAt).toLocaleDateString()}</Td>}
                    <Td>{l.status !== "CONVERTED" && (
                      <Button size="sm" variant="outline" onClick={() => convert(l.id)}>Convert</Button>
                    )}</Td>
                    <Td>
                      <Button variant="ghost" size="icon" onClick={() => del(l.id, l.name)} aria-label="Delete">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </Td>
                  </Tr>
                )})}
              </Tbody>
            </Table>
          </div>

          <PaginationBar
            page={page} pageSize={pageSize} total={total}
            onPageChange={setPage}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          />
        </div>
      </div>

      {/* --- Bulk action modals --- */}
      <BulkModal
        open={bulkMode === "delete"}
        onClose={() => setBulkMode(null)}
        title="Mass Delete Leads"
        description={`Delete ${sel.count} selected leads? This cannot be undone.`}
        confirmLabel={`Delete ${sel.count}`}
        confirmDanger
        busy={bulkMut.isPending}
        onConfirm={() => bulkMut.mutate()}
        error={bulkError}
      >
        <div />
      </BulkModal>

      <BulkModal
        open={bulkMode === "transfer"}
        onClose={() => setBulkMode(null)}
        title="Mass Transfer"
        description={`Reassign ${sel.count} leads to a new owner.`}
        confirmLabel={`Transfer ${sel.count}`}
        confirmDisabled={!transferOwnerId}
        busy={bulkMut.isPending}
        onConfirm={() => bulkMut.mutate()}
        error={bulkError}
      >
        <Label htmlFor="owner">New owner</Label>
        <Select id="owner" value={transferOwnerId} onChange={(e) => setTransferOwnerId(e.target.value)}>
          <option value="">— Select user —</option>
          {users.data?.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </Select>
      </BulkModal>

      <BulkModal
        open={bulkMode === "update"}
        onClose={() => setBulkMode(null)}
        title="Mass Update"
        description={`Update a single field on ${sel.count} leads.`}
        confirmLabel={`Update ${sel.count}`}
        confirmDisabled={!updateValue}
        busy={bulkMut.isPending}
        onConfirm={() => bulkMut.mutate()}
        error={bulkError}
      >
        <Label htmlFor="upd-field">Field</Label>
        <Select id="upd-field" value={updateField} onChange={(e) => setUpdateField(e.target.value as any)}>
          <option value="status">Status</option>
          <option value="source">Source</option>
          <option value="leadIndustry">Lead industry</option>
          <option value="leadDegree">Lead degree</option>
          <option value="leadGeography">Lead geography</option>
        </Select>
        <Label htmlFor="upd-val" className="mt-3">New value</Label>
        {updateOptions[updateField]?.length ? (
          <Select id="upd-val" value={updateValue} onChange={(e) => setUpdateValue(e.target.value)}>
            <option value="">— Select value —</option>
            {updateOptions[updateField].map((v) => <option key={v} value={v}>{v}</option>)}
          </Select>
        ) : (
          <Input id="upd-val" value={updateValue} onChange={(e) => setUpdateValue(e.target.value)} />
        )}
      </BulkModal>

      <BulkModal
        open={bulkMode === "createTask"}
        onClose={() => setBulkMode(null)}
        title="Create Task"
        description={`Create the same task against ${sel.count} selected leads.`}
        confirmLabel={`Create ${sel.count} tasks`}
        confirmDisabled={!taskSubject.trim()}
        busy={bulkMut.isPending}
        onConfirm={() => bulkMut.mutate()}
        error={bulkError}
      >
        <Label htmlFor="tsk-subj">Subject</Label>
        <Input id="tsk-subj" value={taskSubject} onChange={(e) => setTaskSubject(e.target.value)} placeholder="Follow up on LinkedIn" />
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div>
            <Label htmlFor="tsk-due">Due date</Label>
            <Input id="tsk-due" type="datetime-local" value={taskDueAt} onChange={(e) => setTaskDueAt(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="tsk-pri">Priority</Label>
            <Select id="tsk-pri" value={taskPriority} onChange={(e) => setTaskPriority(e.target.value as any)}>
              <option value="LOW">Low</option>
              <option value="NORMAL">Normal</option>
              <option value="HIGH">High</option>
            </Select>
          </div>
        </div>
        <Label htmlFor="tsk-body" className="mt-3">Notes (optional)</Label>
        <textarea
          id="tsk-body"
          rows={3}
          value={taskBody}
          onChange={(e) => setTaskBody(e.target.value)}
          className="flex w-full rounded-md border bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
      </BulkModal>

      <BulkModal
        open={bulkMode === "convert"}
        onClose={() => setBulkMode(null)}
        title="Mass Convert"
        description={
          sel.count > 25
            ? `Only the first 25 of ${sel.count} selected leads will be converted in this batch.`
            : `Convert ${sel.count} leads into Accounts + Contacts + Deals. Already-converted leads are skipped.`
        }
        confirmLabel={`Convert ${Math.min(sel.count, 25)}`}
        busy={bulkMut.isPending}
        onConfirm={() => bulkMut.mutate()}
        error={bulkError}
      >
        <p className="text-xs text-muted-foreground">
          Each lead becomes: a new Account (from the Company), a new Contact, and a Deal in the Consulting pipeline at the Qualifying stage. You'll own everything that's created.
        </p>
      </BulkModal>
    </>
  );
}

function FindProspectsMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <Button variant="outline" onClick={() => setOpen((o) => !o)}>
        <Sparkles className="mr-1 h-4 w-4" />Find Prospects<ChevronDown className="ml-1 h-3.5 w-3.5" />
      </Button>
      {open && (
        <div className="absolute right-0 top-full z-20 mt-1 w-56 rounded-md border bg-popover py-1 text-sm shadow-lg">
          <Link to="/leads/prospect/apollo" onClick={() => setOpen(false)} className="flex items-start gap-2 px-3 py-2 hover:bg-muted">
            <Sparkles className="mt-0.5 h-4 w-4 text-primary" />
            <div>
              <div className="font-medium">Apollo.io search</div>
              <div className="text-xs text-muted-foreground">275M+ B2B contacts.</div>
            </div>
          </Link>
          <Link to="/leads/prospect/linkedin" onClick={() => setOpen(false)} className="flex items-start gap-2 px-3 py-2 hover:bg-muted">
            <Linkedin className="mt-0.5 h-4 w-4 text-[#0077b5]" />
            <div>
              <div className="font-medium">LinkedIn</div>
              <div className="text-xs text-muted-foreground">Lead Gen webhook or Sales Navigator CSV.</div>
            </div>
          </Link>
          <Link to="/leads/import" onClick={() => setOpen(false)} className="flex items-start gap-2 border-t px-3 py-2 hover:bg-muted">
            <Upload className="mt-0.5 h-4 w-4 text-muted-foreground" />
            <div>
              <div className="font-medium">Import CSV</div>
              <div className="text-xs text-muted-foreground">Any column-mapped CSV.</div>
            </div>
          </Link>
        </div>
      )}
    </div>
  );
}
